export const UNGROUPED_LABEL = 'Ungrouped';

export interface DepartmentGroupableInvoice {
  department_groups?: string[];
}

export interface AmountGroupableInvoice extends DepartmentGroupableInvoice {
  amount: number;
}

export interface DepartmentInvoiceGroup<T extends DepartmentGroupableInvoice = AmountGroupableInvoice> {
  label: string;
  isUngrouped: boolean;
  invoices: T[];
  count: number;
  total: number;
}

// Multi-department invoices intentionally appear in EACH of their departments,
// so per-department totals can sum to more than the client's total past due.
// Department-less invoices collect into a single "Ungrouped" bucket.
export const groupInvoicesByDepartment = <T extends AmountGroupableInvoice>(
  documents: T[],
  getTotal?: (doc: T) => number,
): DepartmentInvoiceGroup<T>[] => {
  const resolveTotal = getTotal ?? ((doc: T) => doc.amount);
  const buckets = new Map<string, T[]>();

  const addTo = (label: string, doc: T) => {
    const existing = buckets.get(label) ?? [];
    existing.push(doc);
    buckets.set(label, existing);
  };

  documents.forEach((doc) => {
    const departments = doc.department_groups ?? [];
    if (departments.length === 0) {
      addTo(UNGROUPED_LABEL, doc);
      return;
    }
    departments.forEach((dept) => addTo(dept, doc));
  });

  const groups: DepartmentInvoiceGroup<T>[] = Array.from(buckets.entries()).map(([label, invoices]) => ({
    label,
    isUngrouped: label === UNGROUPED_LABEL,
    invoices,
    count: invoices.length,
    total: invoices.reduce((sum, inv) => sum + resolveTotal(inv), 0),
  }));

  // Named departments alphabetically; "Ungrouped" always pinned last.
  return groups.sort((a, b) => {
    if (a.isUngrouped) return 1;
    if (b.isUngrouped) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
};

export const hasMultiDepartmentInvoice = (documents: DepartmentGroupableInvoice[]): boolean =>
  documents.some((doc) => (doc.department_groups ?? []).length > 1);
